import type { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { Me } from '@/api/types';
import { useAuth } from '@/stores/auth';
import { useFinishProfile } from '@/stores/finishProfile';
import { Sheet } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { Logo } from '@/components/ui/Logo';
import { setReturnTo } from '@/lib/returnTo';

/**
 * v9 §A3 "Finish your profile". Express accounts (guests who came from a
 * link) are onboarded without gender, country, avatar and interests. Dates
 * work right away; Popin Live (random matching) and filters wait until
 * those are set — the server answers 409 profile_incomplete until then.
 */
export function profileIncomplete(me: Pick<Me, 'profile_complete'> | null | undefined): boolean {
  return !!me && me.profile_complete === false;
}

const WHY = [
  { icon: 'people' as const, text: 'Popin Live matches you with new people. Interests make the first minute easy.' },
  { icon: 'shield' as const, text: 'Gender is private and only powers filters (which work both ways).' },
  { icon: 'clock' as const, text: 'About 30 seconds. Your dates keep working meanwhile.' },
];

function Why() {
  return (
    <ul className="space-y-3">
      {WHY.map((w) => (
        <li key={w.text} className="flex gap-3 text-[15px] text-muted">
          <Icon name={w.icon} size={20} className="mt-0.5 shrink-0 text-fg" /> {w.text}
        </li>
      ))}
    </ul>
  );
}

/** Go to /profile/finish and come back to `from` afterwards. */
function useGoFinish() {
  const navigate = useNavigate();
  const loc = useLocation();
  return (from = loc.pathname + loc.search) => {
    setReturnTo(from);
    navigate('/profile/finish');
  };
}

/** Full-page gate in place of Popin Live (`/match`). */
export function FinishProfileGate() {
  const go = useGoFinish();
  return (
    <div className="flex min-h-[100dvh] flex-col bg-ground" data-testid="finish-profile-gate">
      <header className="mx-auto flex w-full max-w-content items-center justify-between px-4 py-5 sm:px-6">
        <Link to="/home" aria-label="Popin home">
          <Logo />
        </Link>
      </header>
      <main className="mx-auto w-full max-w-xl flex-1 px-4 pb-16 pt-6 sm:px-6">
        <h1 className="font-display text-[40px] leading-[0.9] sm:text-[52px]">Finish your profile to meet new people</h1>
        <p className="mt-3 text-[17px] text-muted">You joined through a date link, so we skipped a few things for Popin Live. Add them now and it’s all yours.</p>
        <div className="mt-6 rounded-card bg-surface p-5">
          <Why />
        </div>
        <div className="mt-6 grid gap-2 sm:grid-cols-[1fr_auto]">
          <Button size="lg" iconRight="chevron-right" onClick={() => go('/match')} data-testid="finish-profile-go">
            Finish my profile
          </Button>
          <Link to="/home" className="inline-flex h-14 items-center justify-center rounded-full px-6 font-semibold text-muted hover:text-fg">
            Not now
          </Link>
        </div>
      </main>
    </div>
  );
}

/** Wraps Popin Live: express accounts see the gate until their profile is complete. */
export function RequireCompleteProfile({ children }: { children: ReactNode }) {
  const me = useAuth((s) => s.me);
  if (profileIncomplete(me)) return <FinishProfileGate />;
  return <>{children}</>;
}

/** Settings: a nudge at the top while an express account's profile is incomplete. */
export function FinishProfileCallout() {
  const me = useAuth((s) => s.me);
  const go = useGoFinish();
  if (!profileIncomplete(me)) return null;
  return (
    <section className="flex flex-wrap items-center justify-between gap-4 rounded-card bg-surface p-5" data-testid="settings-finish-profile">
      <div className="min-w-0">
        <p className="text-[17px] font-semibold">Finish your profile</p>
        <p className="text-[15px] text-muted">Add a few details for Popin Live and filters. Your dates already work.</p>
      </div>
      <Button onClick={() => go('/settings')} data-testid="settings-finish-profile-go">
        Finish my profile
      </Button>
    </section>
  );
}

/** The sheet for filters (and any 409 profile_incomplete); hosted once in App. */
export function FinishProfileHost() {
  const open = useFinishProfile((s) => s.open);
  const close = useFinishProfile((s) => s.close);
  const go = useGoFinish();
  return (
    <Sheet open={open} onClose={close} title="Finish your profile" description="Popin Live and filters need a couple more details." testId="finish-profile-sheet">
      <Why />
      <div className="mt-5 grid gap-2 sm:grid-cols-2">
        <Button variant="outline" accent="paper" onClick={close}>
          Not now
        </Button>
        <Button
          onClick={() => {
            close();
            go();
          }}
          data-testid="finish-profile-sheet-go"
        >
          Finish my profile
        </Button>
      </div>
    </Sheet>
  );
}
